import React, { useMemo, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  withStyles,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  CircularProgress,
} from '@material-ui/core'
import { Icon } from '@iconify/react'
import { Link as RouterLink } from 'react-router-dom'
import moment from 'moment-timezone'
import styles from './style'
import translator from '../../translate'
import { customerPortal } from '../../api/limit'
import { LimitTypeEnum } from 'src/model/limit'
import UserType from 'src/model/user-types'
import { track } from '@amplitude/analytics-browser'
import { CoreButton, CoreIconButton } from 'src/components/core'
import { userLimitsGetAction } from 'src/reducers/user-limits/action'
import { selectUserLimits } from 'src/reducers/user-limits/selector'

const PORTAL_ROLE_TYPES = [
  'Elite',
  'Pro',
  'Premium v2',
  'Premium Plus v2',
  'Plus',
  'Pro v2',
  'Launch',
  'Essentials',
  'Growth',
  'Scale',
  'Enterprise',
]

const BILLING_ALERT_ROLE_TYPES = [
  'Premium',
  'Super Admin',
  'Premium Admin Account (Quarterly)',
  'Premium Admin Account (Starter)',
  'Starter - Legacy',
  'Starter',
  'Starter - A',
  'Premium - A',
]

const UPGRADE_ROLE_TYPES = ['Basic']

const CREDITS_INFO_LIST = [
  'Generating a course title on AI Course Creator will require 1 credit.',
  'Generating a course outline on AI Course Creator will require 1 credit.',
  "Bringing the course based on an outline will require 2 credits for every card formed. If you've turned off AI-images for that course, it will take 1 credit for each card.",
  'Using AI-Assistant inside content cards with +++ will require 1 AI credit.',
  'Using in-card AI image generator with your own prompts will require 5 AI credits.',
]

const getInitials = ({ name, surName, email }) => {
  const fullname = `${name || ''} ${surName || ''}`.trim()
  if (fullname) {
    const [first = '', second = ''] = fullname.split(/\s+/)
    return `${first[0] || ''}${second[0] || ''}`.trim() || (first[0] || 'U')
  }

  if (email) {
    return email.slice(0, 2).toUpperCase()
  }

  return 'U'
}

const formatRenewalDate = value => {
  if (!value) return 'Not available'

  const parsed = moment(value)
  if (!parsed.isValid()) return 'Not available'

  return parsed.add(30, 'days').format('Do [of] MMMM')
}

const getTrialDaysRemaining = me => {
  if (!me) return null

  const rawValue =
    me.trialDaysRemaining ??
    me.remainingTrialDays ??
    me.trialDayRemaining ??
    me.trialDaysLeft ??
    me.trialRemainingDays ??
    null

  if (rawValue == null || rawValue === '') return null

  const days = Number(rawValue)
  return Number.isNaN(days) ? null : days
}

const ProfileInformation = props => {
  const { classes } = props
  const dispatch = useDispatch()
  const userLimits = useSelector(selectUserLimits)
  const user = useSelector(state => state.user?.entity)
  const me = useSelector(state => state.authentication?.me)

  const [loading, setLoading] = useState(0)
  const [openCreditsDialog, setOpenCreditsDialog] = useState(false)
  const [openBillingDialog, setOpenBillingDialog] = useState(false)
  const [openRenewalDialog, setOpenRenewalDialog] = useState(false)

  useEffect(() => {
    if (!userLimits) {
      dispatch(userLimitsGetAction())
    }
  }, [dispatch, userLimits])

  const roleType = useMemo(() => {
    if (userLimits?.length) {
      return userLimits[0].limit.roleType
    }
    return ''
  }, [userLimits])

  const aiLimitData = useMemo(() => {
    if (!userLimits) return null
    return userLimits.find(x => x.limit.type === LimitTypeEnum.AICourseCreation)
  }, [userLimits])

  const roleName = UserType[roleType] || 'Unknown'
  const displayRoleName = UserType.getDisplayRoleName(roleName)
  const isTrialUser = UserType.isTrialEquivalent(roleName)
  const trialDaysRemaining = getTrialDaysRemaining(me)
  const aiUsed = aiLimitData?.count ?? 0
  const aiLimit = aiLimitData?.limit?.upperLimit ?? 0
  // Share of this cycle's credits used, for the progress bar.
  const aiUsedPercent =
    aiLimit > 0 ? Math.min(100, Math.round((aiUsed / aiLimit) * 100)) : 0
  const renewalDate = formatRenewalDate(aiLimitData?.refreshedAt)

  const userName = `${user?.name || window.CONSTANTS.NAME || ''} ${
    user?.surName || window.CONSTANTS.SURNAME || ''
  }`.trim()
  const userEmail = user?.email || window.CONSTANTS.EMAIL || ''
  const initials = getInitials({
    name: user?.name || window.CONSTANTS.NAME || '',
    surName: user?.surName || window.CONSTANTS.SURNAME || '',
    email: userEmail,
  })

  const openPortal = () => {
    setLoading(1)
    customerPortal()
      .then(response => {
        const sessionUrl = response.data.sessionUrl
        window.open(`${sessionUrl}`)
      })
      .finally(() => {
        setLoading(0)
      })
  }

  const shouldShowPortalButton = PORTAL_ROLE_TYPES.includes(roleName)
  const shouldShowBillingAlertButton = BILLING_ALERT_ROLE_TYPES.includes(roleName)
  const shouldShowUpgradeButton = UPGRADE_ROLE_TYPES.includes(roleName)

  return (
    <div className={classes.wrapper}>
      <Typography className={classes.pageTitle}>
        {translator._('profile.profile')}
      </Typography>

      <section className={classes.card}>
        <Typography className={classes.cardHeader}>Personal info</Typography>
        <div className={classes.divider} />
        <div className={classes.personalRow}>
          <div className={classes.avatar}>{initials}</div>
          <div className={classes.userInfo}>
            <Typography className={classes.userName}>
              {userName || 'User'}
            </Typography>
            <Typography className={classes.userEmail}>
              {userEmail || '-'}
            </Typography>
          </div>

          {shouldShowPortalButton && (
            <CoreButton
              variant="contained"
              color="primary"
              size="large"
              onClick={openPortal}
              className={classes.personalCta}
            >
              {translator._('profile.paymentDetails')}
            </CoreButton>
          )}

          {shouldShowBillingAlertButton && (
            <CoreButton
              variant="contained"
              color="primary"
              size="large"
              onClick={() => setOpenBillingDialog(true)}
              className={classes.personalCta}
            >
              {translator._('profile.paymentDetails')}
            </CoreButton>
          )}

          {shouldShowUpgradeButton && (
            <CoreButton
              variant="contained"
              color="primary"
              size="large"
              to="/pricing"
              className={classes.personalCta}
              startIcon={
                <Icon
                  size="small"
                  icon="fa6-solid:crown"
                  color="#f7b155"
                  width={15}
                />
              }
              component={RouterLink}
              onClick={() => {
                track('limit_warning_upgrade', {
                  from: 'account_page_upgrade',
                })
              }}
            >
              {translator._('general.upgrade')}
            </CoreButton>
          )}
        </div>

        {isTrialUser && (
          <div className={classes.trialBanner}>
            <Typography className={classes.trialBannerText}>
              {trialDaysRemaining != null
                ? `Your trial plan will end in ${trialDaysRemaining} day${
                    trialDaysRemaining === 1 ? '' : 's'
                  }.`
                : 'Your trial plan will end soon.'}
            </Typography>
            <CoreButton
              variant="contained"
              color="warning"
              to="/pricing"
              component={RouterLink}
              className={classes.trialBannerButton}
              onClick={() => {
                track('limit_warning_upgrade', {
                  from: 'account_page_upgrade',
                })
              }}
            >
              Upgrade Now
            </CoreButton>
          </div>
        )}
      </section>

      <section className={classes.detailsCard}>
        <div className={classes.detailsHeaderRow}>
          <Typography className={classes.cardHeader}>Account details</Typography>
          <RouterLink to="/pricing" className={classes.seePlansButton}>
            {translator._('profile.seePlans')}
          </RouterLink>
        </div>
        <div className={classes.divider} />

        <div className={classes.row}>
          <Typography className={classes.leftLabel}>Plan type</Typography>
          <Typography className={classes.rightValue}>{displayRoleName}</Typography>
        </div>

        <div className={classes.row}>
          <Typography className={classes.leftLabel}>
            AI credits (used / monthly limit)
            <CoreIconButton
              className={classes.infoIconButton}
              onClick={() => setOpenCreditsDialog(true)}
              variant="text"
              color="primary"
              size="small"
            >
              <Icon icon="eva:info-outline" width={20} />
            </CoreIconButton>
          </Typography>
          <Typography className={classes.rightValue}>
            {aiUsed}/{aiLimit}
          </Typography>
        </div>

        <div className={classes.row}>
          <Typography className={classes.leftLabel}>
            AI credits renewal date
            <CoreIconButton
              className={classes.infoIconButton}
              onClick={() => setOpenRenewalDialog(true)}
              variant="text"
              color="primary"
              size="small"
            >
              <Icon icon="eva:info-outline" width={20} />
            </CoreIconButton>
          </Typography>
          <Typography className={classes.rightValue}>{renewalDate}</Typography>
        </div>

        <div className={classes.usage}>
          <div className={classes.usageHead}>
            <span>AI credits used this cycle</span>
            <span>{aiUsedPercent}%</span>
          </div>
          <div
            className={classes.usageTrack}
            role="progressbar"
            aria-valuenow={aiUsedPercent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className={classes.usageFill}
              style={{ width: `${aiUsedPercent}%` }}
            />
          </div>
        </div>
      </section>

      {loading == 1 && <CircularProgress className={classes.loading} />}

      <Dialog
        open={openCreditsDialog}
        onClose={() => setOpenCreditsDialog(false)}
        classes={{ paper: classes.modalPaper }}
      >
        <DialogContent className={classes.modalContent}>
          <Typography className={classes.modalTitle}>
            Here is how AI-credits work:
          </Typography>
          <Typography className={classes.modalBody}>
            We have transactions based on activity and character count on the
            backlog.
          </Typography>
          <ul className={classes.modalBody + ' ' + classes.modalList}>
            {CREDITS_INFO_LIST.map(item => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <Typography className={classes.modalBody}>
            Number of AI credits used for each course generation changes
            based on outline length and AI-image generation frequency.
          </Typography>
        </DialogContent>
        <DialogActions className={classes.modalActions}>
          <CoreButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setOpenCreditsDialog(false)}
          >
            Okay
          </CoreButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openRenewalDialog}
        onClose={() => setOpenRenewalDialog(false)}
        classes={{ paper: classes.modalPaper }}
      >
        <DialogContent className={classes.modalContent}>
          <Typography className={classes.modalTitle}>
            AI Credits Renewal Date
          </Typography>
          <Typography className={classes.modalBody}>
            AI credits renew every 30 days, and the day of renewal is determined
            based on first usage date.
          </Typography>
        </DialogContent>
        <DialogActions className={classes.modalActions}>
          <CoreButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setOpenRenewalDialog(false)}
          >
            Okay
          </CoreButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openBillingDialog}
        onClose={() => setOpenBillingDialog(false)}
        classes={{ paper: classes.modalPaper }}
      >
        <DialogContent className={classes.modalContent}>
          <Typography className={classes.modalTitle}>
            We are having an issue with the subscription service provider.
          </Typography>
          <Typography className={classes.modalBody}>
            If you want to cancel your subscription, upgrade, or downgrade,
            please reach us through the widget right-below or send an email to
            support@minicoursegenerator.com. We&apos;ll handle your request
            immediately.
          </Typography>
        </DialogContent>
        <DialogActions className={classes.modalActions}>
          <CoreButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setOpenBillingDialog(false)}
          >
            {translator._('setPassword.ok')}
          </CoreButton>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default withStyles(styles)(ProfileInformation)
